/**
 * The `protocol` block of a projected request — what a gRPC or WebSocket
 * request actually uses, which the HTTP projection never saw (card 5258c71d).
 *
 * The same rules as `requestProjection.js`, and it reuses its gates rather
 * than growing new ones:
 *
 *   * the gRPC address goes through `projectUrl` (userinfo stripped, query
 *     rebuilt, path through `redactString`) — and because a gRPC address is
 *     usually `host:port` with NO scheme, an `user:pass@` prefix is stripped
 *     here first, since `projectUrl`'s userinfo rule needs a scheme to see it;
 *   * gRPC metadata and WebSocket headers go through `projectKvRows(…,
 *     'header')`, so `authorization: Bearer sk_live…` is `<hidden>` and
 *     `content-type` is shown, exactly as for an HTTP header;
 *   * the gRPC message is a key SHAPE (`shapeOfJson`), never content;
 *   * the .proto path is a LOCAL path (`C:\Users\<name>\…`) and only its
 *     basename leaves;
 *   * service / method names are copied only when they are identifiers;
 *   * WebSocket settings are key NAMES only.
 *
 * Built key by key, never spread — the sentinel sweep in
 * `mcpProtocolProjection.test.js` plants a secret in every value.
 *
 * ## What each request type really uses
 *
 * Read off the builders, not assumed: `GRPCBuilder.js` sends address /
 * service / method / message / metadata / TLS / proto and nothing else;
 * `WebSocketBuilder.js` sends the url (which must start with ws:// or wss://),
 * `wsHeaders` and `wsSettings`; `SSEBuilder.js` sends url, method, headers,
 * body and its OWN auth (`{type:'bearer', token}`, never inherited). All of
 * them substitute `{{variables}}` with the HTTP resolver (gRPC: address,
 * metadata, message; WebSocket: url, wsHeaders, outgoing messages — see
 * utils/protocolVariables), so the block says `variablesSubstituted: true`.
 */
import { HIDDEN, projectKvRows, projectRequest, projectUrl, shapeOfJson } from './requestProjection';
import { templateRefNames } from './templateRefs';

/** The request kinds the app distinguishes; anything else is sent as HTTP. */
export function requestKind(request) {
  const type = String(request?.type || 'http');
  return type === 'grpc' || type === 'websocket' || type === 'sse' ? type : 'http';
}

/**
 * The `request_update` fields a request of each kind IGNORES. Writing one is a
 * silent no-op the user would only discover on send, so it is refused
 * (`unsupported_for_type`). `name` and `description` apply to every kind.
 *
 *   * grpc      — no url (it uses `grpcAddress`), method, headers, params,
 *                 body or auth.
 *   * websocket — url yes; no method, headers (it has `wsHeaders`), params,
 *                 body or auth.
 *   * sse       — url, method, headers and body yes; no params, and no `auth`
 *                 because the SSE builder reads `auth.token` and this tool
 *                 writes the HTTP shape (`auth.bearer`), which it never sends.
 */
export const UNSUPPORTED_FIELDS_BY_KIND = Object.freeze({
  http: Object.freeze([]),
  grpc: Object.freeze(['method', 'url', 'headers', 'params', 'body', 'auth']),
  websocket: Object.freeze(['method', 'headers', 'params', 'body', 'auth']),
  sse: Object.freeze(['params', 'auth']),
});

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_.]*$/;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
/** `user:pass@host:port` with no scheme — the shape `projectUrl` cannot see. */
const BARE_USERINFO_RE = /^[^/?#\s@]*@/;

/** A proto service / method name: an identifier, or `<hidden>`. */
function identifierOrHidden(value) {
  if (typeof value !== 'string' || !value) return null;
  return IDENTIFIER_RE.test(value) ? value : HIDDEN;
}

/** `C:\Users\me\protos\user.proto` → `user.proto`. A local path never leaves. */
export function protoBasename(path) {
  if (typeof path !== 'string' || !path.trim()) return null;
  const parts = path.trim().split(/[\\/]/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

function projectAddress(rawAddress) {
  const raw = typeof rawAddress === 'string' ? rawAddress.trim() : '';
  let stripped = false;
  const withoutUserinfo = SCHEME_RE.test(raw)
    ? raw
    : raw.replace(BARE_USERINFO_RE, () => {
        stripped = true;
        return '';
      });
  const projected = projectUrl(withoutUserinfo, []);
  return {
    address: projected.url,
    credentialsStripped: stripped || projected.credentialsStripped,
    variables: [
      ...(projected.variables || []),
      ...projected.params.flatMap((p) => p.variables || []),
    ],
  };
}

function projectMessage(rawJson) {
  const text = typeof rawJson === 'string' ? rawJson : '';
  let bytes = 0;
  try {
    bytes = new TextEncoder().encode(text).length;
  } catch {
    bytes = text.length;
  }
  if (!text.trim()) return { bytes, parsed: false, shape: null };
  try {
    return { bytes, parsed: true, shape: shapeOfJson(JSON.parse(text)) };
  } catch {
    return { bytes, parsed: false, shape: null };
  }
}

/** `{{refs}}` that survived into an already-safe shape (pointer-only leaves). */
function shapeRefs(shape, out = []) {
  if (typeof shape === 'string') {
    for (const name of templateRefNames(shape)) if (!out.includes(name)) out.push(name);
  } else if (Array.isArray(shape)) {
    for (const item of shape) shapeRefs(item, out);
  } else if (shape && typeof shape === 'object') {
    for (const value of Object.values(shape)) shapeRefs(value, out);
  }
  return out;
}

function unique(names) {
  const out = [];
  for (const name of names) if (!out.includes(name)) out.push(name);
  return out;
}

/**
 * The protocol block for one request: `null` for a plain HTTP request, the
 * kind's own fields otherwise. `variables` lists the `{{references}}` the
 * block carries, so `projectRequestWithProtocol` can fold them into the
 * request's own list.
 */
export function projectProtocol(request) {
  const kind = requestKind(request);
  const ignoredFields = [...UNSUPPORTED_FIELDS_BY_KIND[kind]];

  if (kind === 'grpc') {
    const address = projectAddress(request?.grpcAddress);
    const metadata = projectKvRows(request?.grpcMetadata, 'header');
    const message = projectMessage(request?.grpcRequestJson);
    return {
      kind,
      address: address.address,
      addressCredentialsStripped: address.credentialsStripped,
      useTls: request?.grpcUseTLS === true,
      service: identifierOrHidden(request?.grpcService),
      method: identifierOrHidden(request?.grpcMethod),
      protoFile: protoBasename(request?.protoPath),
      metadata,
      message,
      variablesSubstituted: true,
      ignoredFields,
      variables: unique([
        ...address.variables,
        ...metadata.flatMap((m) => m.variables || []),
        ...shapeRefs(message.shape),
      ]),
    };
  }

  if (kind === 'websocket') {
    const headers = projectKvRows(request?.wsHeaders, 'header');
    const settings = request?.wsSettings;
    return {
      kind,
      headers,
      settingKeys:
        settings && typeof settings === 'object' && !Array.isArray(settings)
          ? Object.keys(settings).sort()
          : [],
      variablesSubstituted: true,
      ignoredFields,
      variables: unique(headers.flatMap((h) => h.variables || [])),
    };
  }

  if (kind === 'sse') {
    return {
      kind,
      // SSE uses the HTTP url / method / headers / body above; its auth is its
      // own bearer token and is never inherited from a folder or collection.
      authInherits: false,
      variablesSubstituted: true,
      ignoredFields,
      variables: [],
    };
  }

  return null;
}

/**
 * `projectRequest` plus its `protocol` block — what `request_read` returns.
 *
 * A wrapper rather than a line inside `projectRequest` so the import graph
 * stays one-directional (this module needs the projection's gates; the
 * projection does not need this module). The result is still built key by
 * key: the projected request's own keys, then `protocol`, and `variables`
 * re-derived as the union of both lists.
 */
export function projectRequestWithProtocol(request, options) {
  const projected = projectRequest(request, options);
  const protocol = projectProtocol(request);
  if (!protocol) return { ...projected, protocol: null };
  return {
    ...projected,
    protocol,
    variables: unique([...projected.variables, ...protocol.variables]),
  };
}
